<template>
    <div>
        <app-dialog
            v-model="isOpen"
            size="lg"
            :persistent="isLoading"
            @close="onClose"
        >
            <template #header>
                <div
                    v-if="isOpenImageCreator"
                    class="flex items-center justify-between border-b border-theme-border pb-4"
                >
                    <div class="flex items-center gap-3">
                        <app-button
                            v-if="
                                enabledCreate &&
                                $ability.hasPermission('image:Create')
                            "
                            circle
                            icon
                            title="Volver a la galería"
                            @click.stop="isOpenImageCreator = false"
                        >
                            <chevron-left-icon class="h-5 w-5" />
                        </app-button>

                        <div class="text-left">
                            <h3 class="text-lg font-semibold text-theme-base">
                                Crear nueva imagen
                            </h3>

                            <p class="text-sm text-theme-muted">
                                Sube una imagen desde tu equipo.
                            </p>
                        </div>
                    </div>
                </div>
                <div v-else class="px-2 border-b-2">
                    <div
                        class="mb-5 flex flex-col gap-4 rounded-xl md:flex-row md:items-center md:justify-between"
                    >
                        <div class="text-left">
                            <h2
                                class="text-lg font-semibold text-theme-primary"
                            >
                                Selecciona una imagen
                            </h2>
                            <p class="mt-1 text-sm text-theme-muted">
                                Elige una imagen existente o carga una nueva.
                            </p>
                        </div>

                        <div class="flex items-center gap-2">
                            <app-button
                                v-if="
                                    enabledCreate &&
                                    $ability.hasPermission('image:Create')
                                "
                                circle
                                icon
                                title="Cargar nueva imagen"
                                @click.stop="isOpenImageCreator = true"
                            >
                                <plus-icon />
                            </app-button>

                            <app-button
                                :disabled="!selectedImage"
                                class="px-5"
                                @click="pushNewImage"
                            >
                                <check-icon /> Seleccionar
                            </app-button>
                        </div>
                    </div>

                    <div v-show="message" class="mt-4">
                        <p>{{ message }}</p>
                    </div>
                </div>
            </template>
            <div>
                <div v-if="isOpenImageCreator">
                    <image-creator
                        :default-alt="defaultAlt"
                        :allow-edit-alt="allowEditAlt"
                        :default-size="defaultSize"
                        @loading="onLoading"
                        @created="selectionUploadImage"
                    />
                </div>
                <div v-else class="mx-auto w-full max-w-6xl">
                    <!-- Toolbar -->
                    <div class="mb-5 flex flex-col gap-2">
                        <div
                            class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
                        >
                            <div
                                class="flex w-full max-w-md items-center gap-2"
                            >
                                <custom-input
                                    v-model="keyword"
                                    type="text"
                                    placeholder="Buscar imágenes..."
                                    class="flex-1"
                                    @click-icon="advanceSearch"
                                    @enter="advanceSearch"
                                >
                                    <template #icon> 🔎 </template>
                                </custom-input>

                                <app-button
                                    icon
                                    circle
                                    color="brand-purple"
                                    title="Actualizar"
                                    @click="reloadItems(true)"
                                >
                                    <reload-icon class="h-5 w-5 text-white" />
                                </app-button>
                            </div>

                            <custom-pagination
                                v-model="currentPage"
                                :length="pagesNumber"
                                @input="listItems()"
                            />
                        </div>

                        <button
                            type="button"
                            class="self-start text-xs text-theme-muted hover:underline"
                            @click="toggleSharedBank"
                        >
                            {{
                                viewingSharedBank
                                    ? "← Volver a mis imágenes"
                                    : "Ver banco de imágenes compartidas"
                            }}
                        </button>

                        <image-size-filter-chip
                            v-if="activeSizeFilter"
                            :size="activeSizeFilter"
                            @clear="clearSizeFilter"
                        />
                    </div>

                    <!-- Images -->
                    <div
                        v-if="imagesList.length"
                        class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4"
                    >
                        <button
                            v-for="(item, index) in imagesList"
                            :key="index"
                            class="group relative overflow-hidden rounded-xl border-2 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                            :class="
                                selectedImage === item.id
                                    ? 'border-stack-primary ring-2 ring-stack-primary'
                                    : 'border-gray-200 hover:border-stack-primary'
                            "
                            @click="selectionImage(item.id)"
                        >
                            <img
                                v-img-loading
                                class="aspect-square w-full object-cover"
                                loading="lazy"
                                :src="item.url | image"
                                :alt="item.alt"
                            />

                            <div
                                class="absolute inset-x-0 bottom-0 flex h-15 items-end bg-gradient-to-t from-black to-transparent p-3"
                            >
                                <div
                                    class="flex w-full items-center justify-between gap-2"
                                >
                                    <p
                                        class="truncate text-sm font-semibold text-white"
                                    >
                                        {{ item.alt || "Sin nombre" }}
                                    </p>

                                    <a
                                        :href="`/app/images/detail/${item.id}`"
                                        target="_blank"
                                        rel="noopener"
                                        class="opacity-0 transition group-hover:opacity-100"
                                        @click.stop
                                    >
                                        <pencil-icon
                                            class="h-6 w-6 text-white"
                                        />
                                    </a>
                                </div>
                            </div>

                            <div
                                v-if="selectedImage === item.id"
                                class="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-stack-primary shadow"
                            >
                                <check-icon class="h-4 w-4 text-white" />
                            </div>
                        </button>
                    </div>

                    <div
                        v-else
                        class="mx-auto mt-10 max-w-md rounded-lg border border-theme-border bg-theme-hover p-8 text-center"
                    >
                        <p class="font-semibold text-theme-base">
                            No encontramos imágenes.
                        </p>

                        <p
                            v-if="activeSizeFilter"
                            class="mt-2 text-sm text-theme-muted"
                        >
                            Solo se están mostrando imágenes de un tamaño. Quita
                            el filtro para ver todas tus imágenes.
                        </p>
                        <p v-else class="mt-2 text-sm text-theme-muted">
                            Intenta cambiar los filtros o realizar otra
                            búsqueda.
                        </p>
                    </div>
                </div>
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import CheckIcon from "@/components/icons/CheckIcon.vue";
import PencilIcon from "@/components/icons/PencilIcon.vue";
import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImageCreator from "@/components/utils/ImageCreator.vue";
import ImageSizeFilterChip from "@/components/utils/ImageSizeFilterChip.vue";

import ChevronLeftIcon from "../icons/ChevronLeftIcon.vue";
import PlusIcon from "../icons/PlusIcon.vue";

export default {
    name: "ImageSelector",
    components: {
        AppDialog,
        CheckIcon,
        CustomInput,
        ImageCreator,
        ImageSizeFilterChip,
        PencilIcon,
        ReloadIcon,
        PlusIcon,
        ChevronLeftIcon
    },
    model: {
        prop: "itemCurrent",
        event: "change"
    },
    props: {
        itemCurrent: {
            default: () => false,
            type: Boolean,
            required: true
        },
        enabledCreate: {
            default: () => false,
            type: Boolean,
            required: false
        },
        defaultFilters: {
            default: () => ({}),
            type: Object,
            required: false
        },
        message: { default: () => "", type: String, required: false },
        defaultAlt: { type: String, default: () => "" },
        allowEditAlt: { type: Boolean, default: () => true },
        defaultSize: { type: String, default: () => "" }
    },
    emits: ["input", "change"],
    data: () => ({
        selectedImage: 0,
        isOpen: false,
        isOpenImageCreator: false,
        isLoading: false,
        currentPage: 1,
        pagesNumber: 0,
        imagesList: [],
        itemsNumber: 0,
        backup: {
            items: [],
            pagination: { pagesNumber: 1, itemsNumber: 1 }
        },
        keyword: "",
        viewingSharedBank: false,
        sizeFilterCleared: false
    }),
    computed: {
        // Filtro de tamaño que impone la pantalla (ej. solo cuadradas para
        // productos); el tenant lo puede quitar para ver todas sus imágenes.
        activeSizeFilter() {
            if (this.viewingSharedBank || this.sizeFilterCleared) return "";
            return this.defaultFilters.size || "";
        },
        ...mapState("images", ["images"]),
        ...mapGetters("control", ["backgroundColor", "borderCardDark"])
    },
    watch: {
        itemCurrent(value) {
            this.setOpen(value);
        },
        keyword(value) {
            if (value) {
                this.imagesList = this.images.filter((item) =>
                    item.alt.includes(value.toLowerCase())
                );
                this.itemsNumber = this.imagesList.length;
                this.pagesNumber = 1;
                this.currentPage = 1;
            } else {
                this.imagesList = [...this.backup.items];
                this.itemsNumber = Number(this.backup.pagination.itemsNumber);
                this.pagesNumber = Number(this.backup.pagination.pagesNumber);
            }
        }
    },
    mounted() {
        this.setOpen(this.itemCurrent);
    },
    methods: {
        ...mapActions("images", [
            "listImagesPaginated",
            "listSharedImagesPaginated"
        ]),
        async loadMoreImages() {
            this.currentPage = this.currentPage + 1;
            await this.listItems();
        },
        async reloadItems() {
            this.keyword = "";
            this.currentPage = 1;
            await this.listItems(true);
        },
        async advanceSearch() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        async clearSizeFilter() {
            this.sizeFilterCleared = true;
            this.keyword = "";
            this.currentPage = 1;
            await this.listItems(true);
        },
        async toggleSharedBank() {
            this.viewingSharedBank = !this.viewingSharedBank;
            this.keyword = "";
            this.currentPage = 1;
            await this.listItems(true);
        },
        async listItems(complete = false) {
            this.isLoading = true;
            try {
                const { items, pagination } = this.viewingSharedBank
                    ? await this.listSharedImagesPaginated({
                          page: this.currentPage,
                          keyword: this.keyword,
                          complete
                      })
                    : await this.listImagesPaginated({
                          ...this.defaultFilters,
                          size: this.activeSizeFilter || undefined,
                          page: this.currentPage,
                          keyword: this.keyword,
                          complete
                      });
                this.imagesList = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.backup = {
                    items,
                    pagination: {
                        itemsNumber: Number(this.itemsNumber),
                        pagesNumber: Number(this.pagesNumber)
                    }
                };
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.isOpen = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        setOpen(value) {
            this.isOpen = value;
            if (value) {
                this.sizeFilterCleared = false;
                this.listItems(true);
            }
        },
        onLoading(value) {
            this.isLoading = value;
        },
        onClose() {
            this.isOpen = false;
            this.isOpenImageCreator = false;
            this.$emit("change", false);
        },
        selectionImage(id) {
            this.selectedImage = id;
        },
        selectionUploadImage(data) {
            this.imagesList.unshift({ ...data, uploaded: false });
            this.selectedImage = data.id;
            this.isOpenImageCreator = false;
        },
        pushNewImage() {
            const image = this.imagesList.find(
                (item) => item.id === this.selectedImage
            );
            this.$emit("input", image || {});
            this.isOpen = false;
            this.$emit("change", false);
        }
    }
};
</script>
